/**
 * Benefit list accordion and the mobile compact sticky header.
 * Compact mode is mobile-only: once the header sticks under the anchor tab,
 * Member / Guest labels hide and Benefit / Description shows. Scrolling back
 * to the header's resting place restores the default header.
 */
class BenefitList extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    this._head = this.querySelector('[data-benefit-list-head]');
    this._anchor = this.querySelector('[data-benefit-list-anchor]');
    this._mq = window.matchMedia('(max-width: 767px)');
    this._onClick = this._onClick.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this.addEventListener('click', this._onClick);
    this._mq.addEventListener('change', this._onScroll);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    this._syncSticky();
  }

  disconnectedCallback() {
    this._mounted = false;
    this.removeEventListener('click', this._onClick);
    this._mq?.removeEventListener('change', this._onScroll);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
    if (this._frame) cancelAnimationFrame(this._frame);
    this._frame = 0;
  }

  /**
   * @param {MouseEvent} event
   */
  _onClick(event) {
    const button = event.target instanceof Element
      ? event.target.closest('[data-benefit-list-toggle]')
      : null;
    if (!button || !this.contains(button)) return;
    const group = button.closest('[data-benefit-list-group]');
    const rows = group?.querySelector(':scope > [data-benefit-list-rows]');
    if (!group || !rows) return;
    const willOpen = group.dataset.open !== 'true';
    group.dataset.open = willOpen ? 'true' : 'false';
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    // Keep the rows in the document so the height can ease. `inert` only
    // takes them out of keyboard and assistive-tech reach while closed.
    rows.inert = !willOpen;
  }

  _onScroll() {
    if (this._frame) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = 0;
      this._syncSticky();
    });
  }

  _syncSticky() {
    if (!this._head || !this._anchor) return;
    if (!this._mq.matches) {
      this._head.classList.remove('is-stuck');
      return;
    }
    const stickyTop = this._stickyTop();
    const anchorTop = this._anchor.getBoundingClientRect().top;
    this._head.classList.toggle('is-stuck', anchorTop <= stickyTop);
  }

  /**
   * @returns {number}
   */
  _stickyTop() {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--anchor-tab-height-mobile')
      .trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}

if (!customElements.get('benefit-list')) {
  customElements.define('benefit-list', BenefitList);
}
